import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { Refresh, Loop as LoopIcon } from "@mui/icons-material";
import { useTheme } from "../../context/ThemeContext";
import batchJobsService from "../../services/batchJobsService";
import {
  formatJobDateTime,
  getJobStatusMeta,
  isJobStale,
} from "./batchJobUtils";
import styles from "./BatchJobs.module.scss";

const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "running", label: "Running" },
  { value: "paused", label: "Paused" },
  { value: "completed", label: "Completed" },
  { value: "completed_with_errors", label: "Completed with errors" },
  { value: "failed", label: "Failed" },
  { value: "cancelled", label: "Cancelled" },
];

const BatchJobs = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // zero-based for TablePagination
  const [pageSize, setPageSize] = useState(20);
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const pollTimerRef = useRef(null);
  const mountedRef = useRef(true);

  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: isDarkMode ? "dark" : "light",
          primary: { main: "#e36250" },
          secondary: { main: "#8b5cf6" },
        },
      }),
    [isDarkMode]
  );

  const fetchJobs = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setLoading(true);
      try {
        const data = await batchJobsService.listJobs({
          status: statusFilter === "all" ? null : statusFilter,
          page: page + 1,
          pageSize,
        });
        if (!mountedRef.current) return;
        setJobs(data.jobs || []);
        setTotal(data.total || 0);
        setError(null);
      } catch (err) {
        if (!mountedRef.current) return;
        setError(err?.message || "Failed to load batch jobs");
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    },
    [statusFilter, page, pageSize]
  );

  useEffect(() => {
    mountedRef.current = true;
    fetchJobs();
    return () => {
      mountedRef.current = false;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [fetchJobs]);

  // Auto-refresh while any visible job is still active.
  useEffect(() => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    const hasActive = jobs.some((job) => job.status === "pending" || job.status === "running");
    const hasPaused = jobs.some((job) => job.status === "paused");
    const interval = hasActive ? 5000 : hasPaused ? 30000 : null;
    if (interval) {
      pollTimerRef.current = setTimeout(() => fetchJobs({ silent: true }), interval);
    }
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [jobs, fetchJobs]);

  return (
    <ThemeProvider theme={muiTheme}>
      <div className={styles.layout}>
        <header className={styles.headerCard}>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <LoopIcon sx={{ color: isDarkMode ? "#c4b5fd" : "#6d28d9" }} />
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Batch Jobs
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
              Background loop-node runs — one agent execution per record of a dataset.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(event) => {
                  setPage(0);
                  setStatusFilter(event.target.value);
                }}
              >
                {STATUS_FILTER_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Refresh />}
              onClick={() => fetchJobs()}
            >
              Refresh
            </Button>
          </Box>
        </header>

        <div className={styles.card}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : jobs.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                No batch jobs yet
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Add a Loop / For Each node to an agent chain and run it on a dataset larger than
                the inline threshold to start a batch job.
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Created</TableCell>
                      <TableCell>Agent</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell sx={{ minWidth: 180 }}>Progress</TableCell>
                      <TableCell align="right">Completed</TableCell>
                      <TableCell align="right">Failed</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {jobs.map((job) => {
                      const statusMeta = getJobStatusMeta(job.status);
                      return (
                        <TableRow
                          key={job.id}
                          hover
                          sx={{ cursor: "pointer" }}
                          onClick={() => navigate(`/batch-jobs/${job.id}`)}
                        >
                          <TableCell>
                            <Typography variant="body2">{formatJobDateTime(job.created_at)}</Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              {String(job.id).slice(0, 8)}…
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">Agent #{job.agent_id}</Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              loop body: #{job.body_agent_id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                              <Chip size="small" label={statusMeta.label} color={statusMeta.color} />
                              {isJobStale(job) && (
                                <Tooltip title="No recent progress heartbeat — the backend watchdog will auto-resume this job.">
                                  <Typography variant="caption" sx={{ color: "warning.main" }}>
                                    recovering…
                                  </Typography>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                value={Math.min(100, Math.max(0, job.progress_pct || 0))}
                                sx={{ height: 6, borderRadius: 3, flexGrow: 1 }}
                              />
                              <Typography variant="caption" sx={{ minWidth: 42, textAlign: "right" }}>
                                {(job.progress_pct || 0).toFixed(0)}%
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">{(job.completed_items || 0).toLocaleString()}</TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              sx={{ color: job.failed_items > 0 ? "error.main" : "inherit", fontWeight: job.failed_items > 0 ? 700 : 400 }}
                            >
                              {(job.failed_items || 0).toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{(job.total_items || 0).toLocaleString()}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                rowsPerPage={pageSize}
                onRowsPerPageChange={(event) => {
                  setPageSize(parseInt(event.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 20, 50, 100]}
              />
            </>
          )}
        </div>
      </div>
    </ThemeProvider>
  );
};

export default BatchJobs;
